import React, { type JSX } from "react";
import { useNavigate } from "react-router";
import type { ConversationSession } from "./types";
import type { EnhancedTurn } from "./useConversationDetail";
import { getTotalCostFromApi, formatCost } from "./modelPricing";
import { formatDateWithZone } from "../../utils/formatDate";
import { formatDuration, getFeedbackIcon, getFeedbackColor } from "./conversationFormatting";

const RESPONSE_PREVIEW_LENGTH = 200;

function turnPath(turn: EnhancedTurn): string {
  return `/admin/conversation/${turn.conversation_id}/turn/${turn.turn_id}`;
}

function SessionBreak({
  sessionIndex,
  sessionCount,
  startedAt,
}: {
  sessionIndex: number;
  sessionCount: number;
  startedAt: string;
}): JSX.Element {
  return (
    <div className="px-6 py-3 bg-amber-50 border-y-2 border-amber-300">
      <div className="flex items-center justify-center space-x-3">
        <div className="h-px flex-1 bg-amber-300"></div>
        <div className="flex items-center space-x-2 text-amber-700">
          <span className="text-sm font-semibold">Session Break</span>
          <span className="text-xs text-amber-600">
            (Session {sessionIndex + 1} of {sessionCount})
          </span>
        </div>
        <div className="h-px flex-1 bg-amber-300"></div>
      </div>
      <p className="text-xs text-amber-600 text-center mt-1">
        New connection started at {formatDateWithZone(startedAt)}
      </p>
    </div>
  );
}

function FirstSessionHeader({
  sessionCount,
  startedAt,
}: {
  sessionCount: number;
  startedAt: string;
}): JSX.Element {
  return (
    <div className="px-6 py-2 bg-blue-50 border-b border-blue-200">
      <div className="flex items-center justify-center space-x-2 text-blue-700">
        <span className="text-xs font-medium">
          Session 1 of {sessionCount} &mdash; started {formatDateWithZone(startedAt)}
        </span>
      </div>
    </div>
  );
}

function TurnMetrics({ turn }: { turn: EnhancedTurn }): JSX.Element {
  return (
    <div className="flex items-center space-x-4">
      {turn.error && (
        <div className="flex items-center space-x-1 text-red-600">
          <span className="text-lg">{"⚠️"}</span>
          <span className="text-sm font-medium">Error</span>
        </div>
      )}
      {turn.hit_limits && (
        <div className="flex items-center space-x-1 text-orange-600">
          <span className="text-lg">{"🚫"}</span>
          <span className="text-sm font-medium">Rate Limited</span>
        </div>
      )}
      <div className="text-right">
        <div className="text-sm text-gray-500">
          {turn.action_count || 0} actions &bull; {turn.output_count || 0} outputs
        </div>
        <div className="text-sm text-gray-500">{formatDuration(turn.total_time_ms)}</div>
        {(turn.total_input_cost_usd || turn.total_output_cost_usd) && (
          <div className="text-sm font-medium text-green-700">
            {formatCost(getTotalCostFromApi(turn.total_input_cost_usd, turn.total_output_cost_usd))}
          </div>
        )}
      </div>
      <div className={`text-lg ${getFeedbackColor(turn.thumbs_up, turn.thumbs_down)}`}>
        {getFeedbackIcon(turn.thumbs_up, turn.thumbs_down)}
      </div>
    </div>
  );
}

function TurnTextBlock({
  label,
  tone,
  children,
}: {
  label: string;
  tone: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-500 mb-1">{label}</label>
      <div className={`${tone} p-3 rounded-md`}>
        <p className="text-sm text-gray-900">{children}</p>
      </div>
    </div>
  );
}

function TurnError({ turn }: { turn: EnhancedTurn }): JSX.Element {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-500 mb-1">Error Status</label>
      <div className="bg-red-50 p-3 rounded-md border border-red-200">
        <div className="flex items-center">
          <span className="text-red-600 mr-2">{"⚠️"}</span>
          <span className="text-sm font-medium text-red-800">This turn encountered an error</span>
        </div>
        {turn.error_text && <p className="text-sm text-red-700 mt-1">{turn.error_text}</p>}
      </div>
    </div>
  );
}

function TurnRow({ turn, index }: { turn: EnhancedTurn; index: number }): JSX.Element {
  const navigate = useNavigate();
  const response = turn.final_chat_response;

  return (
    <div className="p-6 hover:bg-gray-50 cursor-pointer" onClick={() => void navigate(turnPath(turn))}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full">
            <span className="text-sm font-medium text-blue-600">{index + 1}</span>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">Turn {turn.turn_id}</p>
            <p className="text-sm text-gray-500">{formatDateWithZone(turn.created_at)}</p>
          </div>
        </div>

        <TurnMetrics turn={turn} />
      </div>

      <TurnTextBlock label="User Input" tone="bg-gray-50">
        {turn.user_input_text}
      </TurnTextBlock>

      {response && (
        <TurnTextBlock label="Final Response" tone="bg-blue-50">
          {response.length > RESPONSE_PREVIEW_LENGTH
            ? `${response.substring(0, RESPONSE_PREVIEW_LENGTH)}...`
            : response}
        </TurnTextBlock>
      )}

      {turn.feedback_comment && (
        <TurnTextBlock label="Feedback" tone="bg-yellow-50">
          {turn.feedback_comment}
        </TurnTextBlock>
      )}

      {turn.error && <TurnError turn={turn} />}

      <div className="flex justify-end">
        <a
          href={turnPath(turn)}
          onClick={(e) => {
            e.stopPropagation();
          }}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          View Turn Details &rarr;
        </a>
      </div>
    </div>
  );
}

interface ConversationTurnListProps {
  turns: EnhancedTurn[];
  sessions: ConversationSession[];
}

/** All turns across every session, with a marker wherever the session changes. */
export default function ConversationTurnList({
  turns,
  sessions,
}: ConversationTurnListProps): JSX.Element {
  /** Find which session index a turn belongs to */
  const sessionIndexOf = (turn: EnhancedTurn): number =>
    sessions.findIndex((s) => s.conversation_id === turn.conversation_id);

  /** Check if we need a session break before this turn */
  const needsSessionBreak = (turn: EnhancedTurn, index: number): boolean =>
    index > 0 && turns[index - 1].conversation_id !== turn.conversation_id;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">
          All Turns ({turns.length}) across {sessions.length} session
          {sessions.length !== 1 ? "s" : ""}
        </h3>
      </div>

      <div className="divide-y divide-gray-200">
        {turns.map((turn, index) => (
          <React.Fragment key={turn.turn_id}>
            {needsSessionBreak(turn, index) && (
              <SessionBreak
                sessionIndex={sessionIndexOf(turn)}
                sessionCount={sessions.length}
                startedAt={sessions[sessionIndexOf(turn)]?.created_at || turn.created_at}
              />
            )}

            {index === 0 && sessions.length > 1 && (
              <FirstSessionHeader
                sessionCount={sessions.length}
                startedAt={sessions[0]?.created_at || turn.created_at}
              />
            )}

            <TurnRow turn={turn} index={index} />
          </React.Fragment>
        ))}
      </div>

      {turns.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500">No turns found for this project.</div>
        </div>
      )}
    </div>
  );
}
